import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Check, Trash2, Plus, UserPlus } from "lucide-react";

export default function AdminAttendance() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [searchParams] = useSearchParams();
  const nav = useNavigate();

  const loadEvents = () => api.get("/events").then((r) => setEvents(r.data));
  const loadAttendances = () => eventId ? api.get(`/attendance?event_id=${eventId}`).then((r) => setAttendances(r.data)) : setAttendances([]);

  useEffect(() => { loadEvents(); }, []);
  useEffect(() => { loadAttendances(); /* eslint-disable-next-line */ }, [eventId]);

  // Auto-register from URL param
  useEffect(() => {
    const yid = searchParams.get("youth");
    if (yid && eventId) {
      api.post("/attendance", { youth_id: yid, event_id: eventId })
        .then(() => { toast.success("Presença registrada"); loadAttendances(); })
        .catch((err) => toast.error(err.response?.data?.detail || "Erro"));
    }
    /* eslint-disable-next-line */
  }, [searchParams, eventId]);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/youth?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data.slice(0, 10)));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const presentIds = useMemo(() => new Set(attendances.map((a) => a.youth_id)), [attendances]);

  const mark = async (y) => {
    if (!eventId) return toast.error("Selecione um encontro");
    if (presentIds.has(y.id)) return toast.info("Já marcado como presente");
    try {
      await api.post("/attendance", { youth_id: y.id, event_id: eventId });
      toast.success(`${y.full_name} presente ✓`);
      setQ(""); setResults([]);
      loadAttendances();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const unmark = async (aid, name) => {
    if (!confirm(`Remover presença de ${name}?`)) return;
    await api.delete(`/attendance/${aid}`);
    toast.success("Removido");
    loadAttendances();
  };

  const selectedEvent = events.find((e) => e.id === eventId);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <div className="label">Gestão de Jovens</div>
        <h1 className="font-display text-3xl font-black mt-2">Registrar presença</h1>
      </div>

      <div className="ci-card p-5 space-y-3">
        <label className="label block">Encontro</label>
        <select data-testid="attendance-event-select" value={eventId} onChange={(e)=>setEventId(e.target.value)} className="ci-input">
          <option value="">Selecione um evento</option>
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {new Date(e.start_date).toLocaleDateString("pt-BR")} — {e.name}
            </option>
          ))}
        </select>
        {selectedEvent && (
          <div className="text-xs text-slate-400">{selectedEvent.location || "—"} · {selectedEvent.event_type === "encontro" ? "Encontro" : `${selectedEvent.edition_number}ª Edição`}</div>
        )}
      </div>

      {eventId && (
        <>
          <div className="ci-card p-5 space-y-3">
            <label className="label block">Buscar jovem</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="attendance-search" value={q} onChange={(e)=>setQ(e.target.value)} className="ci-input pl-10" placeholder="Nome, WhatsApp..." />
            </div>
            {results.length > 0 && (
              <div className="space-y-2">
                {results.map((y) => (
                  <button key={y.id} onClick={() => mark(y)} data-testid={`attendance-mark-${y.id}`}
                          className="w-full text-left flex items-center justify-between gap-3 bg-black/50 hover:bg-blue-500/10 border border-white/5 hover:border-blue-500/40 rounded-xl px-3 py-2.5 transition-all">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center font-bold text-blue-300 text-sm shrink-0">
                        {y.full_name?.[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-medium">{y.full_name}</div>
                        <div className="text-xs text-slate-400 truncate">{y.church || "—"} · {y.age != null ? `${y.age} anos` : "idade ?"}</div>
                      </div>
                    </div>
                    {presentIds.has(y.id) ? (
                      <span className="text-[10px] font-bold text-green-300 bg-green-500/10 border border-green-500/30 px-2 py-0.5 rounded-full shrink-0">PRESENTE</span>
                    ) : (
                      <Plus size={16} className="text-blue-400 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
            {q.length >= 2 && results.length === 0 && (
              <button onClick={() => nav("/admin/jovens/novo")} className="w-full btn-ghost text-sm"><UserPlus size={14}/> Cadastrar novo jovem</button>
            )}
          </div>

          <div className="ci-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label">Presentes neste encontro</div>
              <span className="text-xs text-slate-400">{attendances.length}</span>
            </div>
            {attendances.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">Ninguém marcado ainda. Use a busca acima.</div>
            ) : (
              <div className="space-y-2">
                {attendances.map((a) => (
                  <div key={a.id} data-testid={`attendance-row-${a.id}`} className="flex items-center justify-between gap-3 bg-black/40 border border-white/5 rounded-lg px-3 py-2.5 text-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <Check size={16} className="text-green-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="truncate font-medium">{a.youth_name}</div>
                        <div className="text-xs text-slate-500">{a.youth_church || "—"} · {a.youth_age ?? "?"} anos</div>
                      </div>
                    </div>
                    <button onClick={() => unmark(a.id, a.youth_name)} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 size={14}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
